// @ts-nocheck
import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RouteConstants } from '../../../Constants/routeConstants';
import { useAppSelector } from '../../../Hooks';
import { setPopup, setSidePanel } from '../../../Redux/slices/metaSlice';
import { addPageAction, setActiveSideSlug, type pageDetailData } from '../../../Redux/slices/pageDetailSlice';
import { login } from '../../../Redux/slices/userSlice';
import { uploadMedia, pageGetGallleryThunk } from '../../../Redux/thunks/pageDetailThunk';
import { updateUserInfo } from '../../../Redux/thunks/settingsThunk';
import RoundedCropper from '../CropperPopup/RoundedCropper';
import './ProfileModal.scss';

interface ProfileModalProps {
  show: boolean;
  handleClose: () => void;
  pageImage?: string;
  userName?: string;
  isInSidePanel?: boolean;
}

const profileQuestions = [
  { 
    id: 1, 
    original: "Who was the biggest influence in your life?",
    firstPerson: "Who was the biggest influence in my life?",
    defaultAnswer: "" 
  },
  { 
    id: 2, 
    original: "What major event or realization shaped who you are?",
    firstPerson: "What major event or realization shaped who I am?",
    defaultAnswer: "" 
  },
  { 
    id: 3, 
    original: "Who was your closest friend growing up?",
    firstPerson: "Who was my closest friend growing up?",
    defaultAnswer: "" 
  },
  { 
    id: 4, 
    original: "What was the most important lesson you learned as a parent?",
    firstPerson: "What was the most important lesson I learned as a parent?",
    defaultAnswer: "" 
  },
  { 
    id: 5, 
    original: "What is the best advice you could give your child?",
    firstPerson: "What is the best advice I could give my child?",
    defaultAnswer: "" 
  },
  { 
    id: 6, 
    original: "What phrase has kept you afloat during hard times?",
    firstPerson: "What phrase has kept me afloat during hard times?",
    defaultAnswer: "" 
  },
  { 
    id: 7, 
    original: "What advice do you have about growing older?",
    firstPerson: "What advice do I have about growing older?",
    defaultAnswer: "" 
  },
  { 
    id: 8, 
    original: "What are some of your favorite memories growing up?",
    firstPerson: "What are some of my favorite memories growing up?",
    defaultAnswer: "" 
  },
  { 
    id: 9, 
    original: "When life was difficult, what did you do to overcome it?",
    firstPerson: "When life was difficult, what did I do to overcome it?",
    defaultAnswer: "" 
  },
  { 
    id: 10, 
    original: "What is one thing you haven't done you regret?",
    firstPerson: "What is one thing I haven't done I regret?",
    defaultAnswer: "" 
  },
  { 
    id: 11, 
    original: "What are you most proud of having done in life?",
    firstPerson: "What am I most proud of having done in life?",
    defaultAnswer: "" 
  },
  { 
    id: 12, 
    original: "Do you have any superstitions?",
    firstPerson: "Do I have any superstitions?",
    defaultAnswer: "" 
  },
  { 
    id: 13, 
    original: "What do you hold sacred?",
    firstPerson: "What do I hold sacred?",
    defaultAnswer: "" 
  },
  { 
    id: 14, 
    original: "Did you have a nickname? What is the story behind it?",
    firstPerson: "Did I have a nickname? What is the story behind it?",
    defaultAnswer: "" 
  },
  { 
    id: 15, 
    original: "If you could redo any period in your life, what would it be and why?",
    firstPerson: "If I could redo any period in my life, what would it be and why?",
    defaultAnswer: "" 
  },
  { 
    id: 16, 
    original: "What is your favorite compliment to receive? Why?",
    firstPerson: "What is my favorite compliment to receive? Why?",
    defaultAnswer: "" 
  }
];

const ProfileModal: React.FC<ProfileModalProps> = ({ show, handleClose, pageImage, userName, isInSidePanel = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userState = useAppSelector(state => state.user);
  const pageDetailState: pageDetailData = useAppSelector((state: any) => state.pagedetail?.data);
  const isLoggedIn = !!userState?.value?.data?._id;
  const [answers, setAnswers] = useState(
    profileQuestions.reduce((acc, q) => ({ ...acc, [q.id]: q.defaultAnswer }), {})
  );
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [showAllQuestions, setShowAllQuestions] = useState(false);
  const [profileDescription, setProfileDescription] = useState(userState?.value?.data?.description || 'Tell us about yourself...');
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  // Image upload states
  const [img, setImg] = useState({
    url: userState?.value?.data?.image || pageImage || '',
    file: '',
    selected_img: '',
  });
  const [showCropper, setShowCropper] = useState(false);
  const [imgValidation, setImgValidation] = useState('');

  // Questions functionality
  const canAction = () => {
    if (!userState?.value?.data?._id) {
      loginRedirect();
    }

    if (
      pageDetailState?.belongsTo?._id === userState?.value?.data?._id ||
      pageDetailState?.subscribtion_status === 'APPROVED'
    ) {
      return true;
    } else {
      return false;
    }
  };

  const loginRedirect = () => {
    navigate(RouteConstants.LOGIN);
  };

  const handleQuestionsClick = () => {
    if (canAction()) {
      // Show questions popup without closing profile modal
      dispatch(setPopup('question-popup'));
      // Prevent event propagation to avoid closing profile modal
      event?.stopPropagation();
    } else {
      dispatch(addPageAction('show-subsciption-step'));
    }
  };

  const handleAnswerChange = (questionId: number, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const saveAnswer = (questionId: number) => {
    // Save answer to user profile
    const payload = {
      ...userState?.value?.data,
      answers: {
        ...userState?.value?.data?.answers,
        [questionId]: answers[questionId]
      }
    };
    delete payload.email;
    dispatch(updateUserInfo(payload, false));

    // Update local state
    dispatch(login({
      ...userState?.value,
      data: { 
        ...userState?.value?.data, 
        answers: {
          ...userState?.value?.data?.answers,
          [questionId]: answers[questionId]
        }
      }
    }));

    setIsEditing(null);
  };

  const handleDescriptionChange = (value: string) => {
    setProfileDescription(value);
  };

  const toggleDescriptionEdit = () => {
    setIsEditingDescription(!isEditingDescription);
  };

  const saveDescription = () => {
    // Save description to user profile
    const payload = {
      ...userState?.value?.data,
      description: profileDescription,
    };
    delete payload.email;
    dispatch(updateUserInfo(payload, false));

    // Update local state
    dispatch(login({
      ...userState?.value,
      data: { ...userState?.value?.data, description: profileDescription }
    }));

    setIsEditingDescription(false);
  };

  const selectImg = () => {
    const input = document.getElementById('profile-image-input');
    if (input) {
      input?.click();
    }
  };

  const typeValidation = (event: any) => {
    setImg((data: any) => {
      return {
        ...data,
        file: '',
      };
    });
    const selectedFile = event.target.files[0];
    const imageFile = selectedFile;
    if (!imageFile.name.match(/\.(jpg|jpeg|png|webp)$/)) {
      setImgValidation('Only jpg | jpeg | png | webp images allowed.');
      return false;
    }
    setImgValidation('');
    if (event.target.files?.length) {
      setImg({
        ...img,
        selected_img: URL.createObjectURL(event?.target?.files[0]),
      });

      const reader: any = new FileReader();
      const file: any = selectedFile;
      reader.readAsDataURL(file);
      setImg((data: any) => {
        return { ...data, file };
      });
      reader.onload = () => {
        setImg((data: any) => {
          return { ...data, selected_img: reader?.result };
        });
        setShowCropper(true);
      };
    }
  };

  const CroppedImage = (e: any) => {
    if (e && e !== 'cancel') {
      setImg((data: any) => {
        return { ...data, url: URL.createObjectURL(e), file: e };
      });
      // Upload the image
      uploadProfileImage(e);
    }
    setShowCropper(false);
  };

  const uploadProfileImage = async (file: any) => {
    if (file && userState?.value?.data?._id) {
      const formData: any = new FormData();
      formData.append('media', file);

      try {
        const response = await uploadMedia(formData, userState?.value?.data?._id);
        if (response?.data?.data[0]?.url) {
          const newImageUrl = response?.data?.data[0]?.url;

          // Update user profile with new image
          const payload = {
            ...userState?.value?.data,
            image: newImageUrl,
          };
          delete payload.email;
          dispatch(updateUserInfo(payload, false)); // Don't reload page

          // Update local state immediately for UI feedback
          dispatch(login({
            ...userState?.value,
            data: { ...userState?.value?.data, image: newImageUrl }
          }));
        }
      } catch (error) {
        console.error('Error uploading profile image:', error);
        setImgValidation('Failed to upload image. Please try again.');
      }
    }
  };

  const handleViewAllQuestions = () => {
    setShowAllQuestions(true);
  };

  const handleGalleryClick = () => {
    if (!userState?.value?.data?._id) {
      loginRedirect();
      return;
    }
    if (canAction()) {
      dispatch(setPopup('gallery-modal'));
    } else {
      dispatch(addPageAction('show-subsciption-step'));
    }
  };

  const renderProfileContent = () => (
    <div className="profile-content">
      {/* Hero Section */}
      <div className="profile-hero">
        <div className="hero-background">
          <div className="gradient-overlay"></div>
        </div>
        <div className="hero-content">
          <div className="profile-image-container">
            <div className="profile-image">
              {img.url || userState?.value?.data?.image || pageImage ? (
                <>
                  <img src={img.url || userState?.value?.data?.image || pageImage} alt="Profile" />
                  {isLoggedIn && (
                    <div className="upload-overlay" onClick={selectImg}>
                      <i className="fa fa-camera"></i>
                      <span>Change Photo</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="profile-initial" onClick={isLoggedIn ? selectImg : undefined}>
                  {userName?.charAt(0) || 'U'}
                  {isLoggedIn && (
                    <div className="upload-overlay">
                      <i className="fa fa-camera"></i>
                      <span>Add Photo</span>
                    </div>
                  )}
                </div>
              )}
              {isLoggedIn && (
                <input
                  type="file"
                  id="profile-image-input"
                  accept="image/*"
                  onChange={typeValidation}
                  style={{ display: 'none' }}
                />
              )}
            </div>
          </div>
          <div className="profile-info">
            <h1 className="profile-name">{userName || 'User Name'}</h1>
            {isLoggedIn && (
              <p className="profile-email">{userState?.value?.data?.email}</p>
            )}
          </div>
        </div>
      </div>

      {/* About Section */}
      <div className="about-section">
        <div className="section-header">
          <h3>About Me</h3>
          {isLoggedIn && (
            <button
              className="edit-section-btn"
              onClick={toggleDescriptionEdit}
              title="Edit description"
            >
              <i className="fa fa-edit"></i>
            </button>
          )}
        </div>
        {isEditingDescription ? (
          <div className="description-edit">
            <textarea
              value={profileDescription}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              placeholder="Tell us about yourself..."
              rows={4}
              className="description-textarea"
            />
            <div className="description-actions">
              <button className="save-btn" onClick={saveDescription}>
                <i className="fa fa-check"></i> Save
              </button>
              <button className="cancel-btn" onClick={toggleDescriptionEdit}>
                <i className="fa fa-times"></i> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="description-display">
            <p className="description-text">
              {profileDescription || 'Tell us about yourself...'}
            </p>
          </div>
        )}
      </div>

      {/* Photos and Videos Section (Gallery Button) */}
      <div className="center-section-wrapper">
        <div className="section-header" onClick={handleGalleryClick} style={{ cursor: 'pointer' }}>
          <h3>Photos and Videos</h3>
          <span className="toggle-arrow" title="Click to toggle">
            Show {pageDetailState?.counts?.gallery || 0} items
          </span>
        </div>
      </div>

      {/* Questions Section */}
      <div className="questions-section">
        <div className="section-header">
          <h3>My Story</h3>
          <p className="section-subtitle">Discover who you are through these meaningful questions</p>
        </div>

        <div className={`questions-grid ${showAllQuestions ? 'expanded' : ''}`}>
          {profileQuestions.slice(0, showAllQuestions ? profileQuestions.length : 6).map((q, index) => (
            <div key={q.id} className={`question-card ${isEditing === q.id ? 'editing' : ''}`}>
              <div className="question-header">
                <div className="question-icon">
                  <i className="fa fa-quote-left"></i>
                </div>
                <h4 className="question-title">
                  {isEditing === q.id ? q.original : q.firstPerson}
                </h4>
              </div>

              <div className="question-content">
                {isEditing === q.id ? (
                  <div className="answer-edit">
                    <textarea
                      value={answers[q.id]}
                      onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                      rows={3}
                      placeholder="Answer it here..."
                      className="answer-textarea"
                    />
                    <div className="answer-actions">
                      <button
                        className="save-answer-btn"
                        onClick={() => saveAnswer(q.id)}
                      >
                        <i className="fa fa-check"></i>
                      </button>
                      <button
                        className="cancel-answer-btn"
                        onClick={() => setIsEditing(null)}
                      >
                        <i className="fa fa-times"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="answer-display" 
                    onClick={() => isLoggedIn && setIsEditing(q.id)}
                    style={{ cursor: isLoggedIn ? 'pointer' : 'default' }}
                  >
                    <p className="answer-text">
                      {answers[q.id] || "Answer it here..."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="view-more-section">
          <button 
            className="view-more-btn" 
            onClick={() => setShowAllQuestions(!showAllQuestions)}
          >
            <span>{showAllQuestions ? 'Show Less' : 'View All Questions'}</span>
            <i className={`fa fa-arrow-${showAllQuestions ? 'up' : 'right'}`}></i>
          </button>
        </div>
      </div>
    </div>
  );

  if (isInSidePanel) {
    return (
      <div className="profile-content-wrapper modern-side-panel">
        <div className="profile-header-bar modern-header">
          <div className="header-content">
            <div className="header-left">
              <h4>My Profile</h4>
              {/* <span className="header-subtitle">Personal story and memories</span> */}
            </div>
            <div className="header-actions">
              {isLoggedIn && pageDetailState?.questions?.length > 0 && (
                <button
                  className="action-btn questions-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleQuestionsClick();
                  }}
                  title="Answer your page questions"
                >
                  <i className="fa fa-questionsicon"></i>
                  <span>Questions</span>
                  <span className="badge">{pageDetailState?.questions?.length}</span>
                </button>
              )}
              {isLoggedIn && (
                <button
                  className="action-btn edit-btn"
                  onClick={() => setIsEditing(isEditing ? null : -1)}
                >
                  <i className="fa fa-edit"></i>
                  <span>{isEditing ? 'Done' : 'Edit'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="side-panel-content">
          {renderProfileContent()}
        </div>
        {imgValidation && (
          <div className="error-message">
            {imgValidation}
          </div>
        )}
        {showCropper && (
          <RoundedCropper
            show={showCropper}
            selectedImg={img.selected_img}
            setCroppedImage={CroppedImage}
            croppershap={'round'}
          />
        )}
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="profile-modal"
      fullscreen
    >
      <Modal.Header closeButton className="modern-header">
        <div className="header-content">
          <div className="header-left">
            <Modal.Title>My Profile</Modal.Title>
          </div>
          <div className="header-actions">
            {isLoggedIn && (
              <button
                className="action-btn questions-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleQuestionsClick();
                }}
                title="Answer your page questions"
              >
                <i className="fa fa-questionsicon"></i>
                <span>Your Questions</span>
                <span className="badge">{pageDetailState?.questions?.length}</span>
              </button>
            )}
            {isLoggedIn && (
              <button
                className="action-btn edit-btn"
                onClick={() => setIsEditing(isEditing ? null : -1)}
              >
                <i className="fa fa-edit"></i>
                <span>{isEditing ? 'Done' : 'Edit'}</span>
              </button>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        {renderProfileContent()}
        {imgValidation && (
          <div className="error-message">
            {imgValidation}
          </div>
        )}
      </Modal.Body>
      {showCropper && (
        <RoundedCropper
          show={showCropper}
          selectedImg={img.selected_img}
          setCroppedImage={CroppedImage}
          croppershap={'round'}
        />
      )}
    </Modal>
  );
};

export default ProfileModal; 